import { useMemo, useState } from "react";
import { useCrm, collabById } from "@/lib/crm";
import { STAGES, eur, eur2, dateIt, compensoSsd, isAcq } from "@/lib/constants";
import { NotePreview } from "@/components/NotePreview";
import { SsdDialog } from "@/components/SsdDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Pencil, Trash2, MapPin, Users2, History, CalendarDays, Euro, Layers, FileText, Search } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AttivitaPanel } from "@/components/AttivitaPanel";

function Avatar({ collab }) {
  if (!collab) return <span className="text-[10px] text-slate-400">Non assegnata</span>;
  const initials = collab.nome.split(" ").map((n) => n[0]).slice(0, 2).join("");
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
      <span className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ background: collab.colore }}>
        {initials}
      </span>
      {collab.nome.split(" ")[0]}
    </span>
  );
}

export default function Pipeline() {
  const { ssd, collaboratori, settings, api, refresh, progetti } = useCrm();
  const comm = settings?.commissione ?? 0.5;
  const [canale, setCanale] = useState("privati");
  const isPartnerTab = settings?.settore === "ritrattistica" && canale === "partner";
  const stagesList = useMemo(() => {
    const st = isPartnerTab ? settings?.stages_partner : settings?.stages;
    return st?.length ? st : STAGES;
  }, [settings, isPartnerTab]);
  const stageDot = (i) => i === stagesList.length - 1 ? "bg-emerald-500" : ["bg-slate-400", "bg-sky-500", "bg-amber-500", "bg-purple-500", "bg-indigo-500", "bg-pink-500", "bg-cyan-500"][i % 7];
  const stageLabel = (key) => (stagesList.find((s) => s.key === key) || {}).label || key;
  const isRitr = settings?.settore === "ritrattistica";
  const [filterProj, setFilterProj] = useState("all");
  const projById = useMemo(() => Object.fromEntries((progetti || []).map((p) => [p.id, p])), [progetti]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [attivitaItem, setAttivitaItem] = useState(null);
  const [filterCollab, setFilterCollab] = useState("all");
  const [search, setSearch] = useState("");
  const list = useMemo(() => (isRitr ? ssd.filter((x) => (canale === "partner" ? x.canale === "partner" : x.canale !== "partner")) : ssd), [ssd, isRitr, canale]);
  const partnerById = useMemo(() => Object.fromEntries(ssd.filter((x) => x.canale === "partner").map((x) => [x.id, x])), [ssd]);

  const columns = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = list.filter((x) => {
      const okCollab = filterCollab === "all" ? true : filterCollab === "none" ? !x.collaboratore_id : x.collaboratore_id === filterCollab;
      const okSearch = !q || [x.nome, x.citta, x.referente, x.disciplina].some((v) => (v || "").toLowerCase().includes(q));
      return okCollab && okSearch;
    });
    return stagesList.map((s, i) => ({ ...s, dot: stageDot(i), items: filtered.filter((x) => x.stage === s.key && (filterProj === "all" || (x.progetto_id || "") === filterProj)) }));
  }, [list, filterCollab, search, stagesList, filterProj]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = async (item, dir) => {
    const idx = stagesList.findIndex((s) => s.key === item.stage);
    const next = idx + dir;
    if (next < 0 || next >= stagesList.length) return;
    try {
      await api.updateSsd(item.id, { stage: stagesList[next].key });
      await refresh();
      toast.success(`"${item.nome}" → ${stagesList[next].label}`);
    } catch (e) {
      toast.error("Impossibile aggiornare lo stato. Riprova.");
    }
  };

  const save = async (data) => {
    if (editing) {
      await api.updateSsd(editing.id, data);
      toast.success(isRitr ? "Scheda aggiornata" : "SSD aggiornata");
    } else {
      await api.createSsd(isRitr ? { ...data, canale } : data);
      toast.success(isRitr ? (isPartnerTab ? "Partner aggiunto" : "Cliente aggiunto") : "SSD aggiunta alla pipeline");
    }
    setOpen(false);
    setEditing(null);
    await refresh();
  };

  const remove = async (item) => {
    await api.deleteSsd(item.id);
    await refresh();
    toast.success(isRitr ? "Scheda eliminata · archiviata in Liste" : "SSD eliminata · archiviata in Liste");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">Pipeline CRM</h1>
          <p className="text-slate-500 mt-1">{isRitr ? (isPartnerTab ? "Collaborazioni con parrucchieri, beauty farm, palestre e attività che rivendono la ritrattistica." : "Clienti singoli con esigenze di ritratto, dal primo contatto all'incasso.") : "Gestisci le società sportive lungo le fasi del funnel."}</p>
        </div>
        <Button data-testid="add-ssd-button" onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full gap-1.5">
          <Plus className="h-4 w-4" /> {isRitr ? (isPartnerTab ? "Nuovo partner" : "Nuovo cliente") : "Nuova SSD"}
        </Button>
      </div>

      {isRitr && (
        <div className="inline-flex rounded-full border border-slate-200 dark:border-slate-800 p-1 bg-white dark:bg-slate-900" data-testid="pipeline-canale-tabs">
          {[{ k: "privati", l: "Clienti Privati" }, { k: "partner", l: "Partner" }].map((t) => (
            <button key={t.k} data-testid={`pipeline-canale-${t.k}`} onClick={() => { setCanale(t.k); setFilterProj("all"); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${canale === t.k ? "bg-primary text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
              {t.l} <span className="tabular-nums opacity-70 ml-1">{ssd.filter((x) => (t.k === "partner" ? x.canale === "partner" : x.canale !== "partner")).length}</span>
            </button>
          ))}
        </div>
      )}

      {collaboratori.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="pipeline-collab-filter">
          <div className="relative w-full sm:w-80 mb-1 sm:mb-0 sm:mr-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="pipeline-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={isRitr ? "Cerca cliente (nome, città, referente…)" : "Cerca società (nome, città, referente…)"} className="pl-9 rounded-full h-9" />
          </div>
          <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Users2 className="h-3.5 w-3.5" /> Collaboratore:</span>
          <button data-testid="pipeline-filter-all" onClick={() => setFilterCollab("all")}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filterCollab === "all" ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
            Tutti <span className="tabular-nums opacity-70">{list.length}</span>
          </button>
          {collaboratori.map((c) => {
            const n = list.filter((x) => x.collaboratore_id === c.id).length;
            const active = filterCollab === c.id;
            return (
              <button key={c.id} data-testid={`pipeline-filter-${c.id}`} onClick={() => setFilterCollab(c.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
                <span className="h-3.5 w-3.5 rounded-full" style={{ background: c.colore }} />
                {c.nome.split(" ")[0]} <span className="tabular-nums opacity-70">{n}</span>
              </button>
            );
          })}
          {list.some((x) => !x.collaboratore_id) && (
            <button data-testid="pipeline-filter-none" onClick={() => setFilterCollab("none")}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filterCollab === "none" ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
              Non assegnate <span className="tabular-nums opacity-70">{list.filter((x) => !x.collaboratore_id).length}</span>
            </button>
          )}
        </div>
      )}

      {isRitr && !isPartnerTab && progetti.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="pipeline-progetto-filter">
          <span className="text-xs text-slate-400">Progetto:</span>
          <button data-testid="pipeline-proj-all" onClick={() => setFilterProj("all")}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filterProj === "all" ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
            Tutti <span className="tabular-nums opacity-70">{list.length}</span>
          </button>
          {progetti.map((p) => {
            const n = ssd.filter((x) => (x.progetto_id || "") === p.id).length;
            return (
              <button key={p.id} data-testid={`pipeline-proj-${p.id}`} onClick={() => setFilterProj(p.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filterProj === p.id ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
                <span className="h-3.5 w-3.5 rounded-full" style={{ background: p.colore }} />
                {p.nome} <span className="tabular-nums opacity-70">{n}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4 -mx-4 px-4">
        {columns.map((col) => (
          <div key={col.key} data-testid={`column-${col.key}`} className="min-w-[280px] w-[280px] flex-shrink-0">
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{col.label}</span>
              </div>
              <span className="text-xs font-bold tabular-nums text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5">{col.items.length}</span>
            </div>

            <div className="space-y-2.5">
              {col.items.map((item) => {
                const collab = collabById(collaboratori, item.collaboratore_id);
                const idx = stagesList.findIndex((s) => s.key === item.stage);
                return (
                  <div key={item.id} data-testid={`ssd-card-${item.id}`}
                    onClick={() => { setEditing(item); setOpen(true); }}
                    className="group cursor-pointer rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3.5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 animate-fade-up">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-slate-900 dark:text-white truncate">{item.nome}</div>
                        <div className="text-xs text-slate-400">{item.disciplina || "—"}</div>
                        {!isRitr && item.portfolio_id && (
                          <span data-testid={`ssd-portfolio-badge-${item.id}`} className="inline-flex items-center gap-1 mt-1 mr-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-900 text-white dark:bg-slate-700">In Portfolio</span>
                        )}
                        {isRitr && item.partner_id && partnerById[item.partner_id] && (
                          <span data-testid={`ssd-partner-badge-${item.id}`} className="inline-flex items-center gap-1 mt-1 mr-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">via {partnerById[item.partner_id].nome}</span>
                        )}
                        {isRitr && item.canale === "partner" && (
                          <span data-testid={`partner-clienti-${item.id}`} className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">{ssd.filter((x) => x.partner_id === item.id).length} clienti portati</span>
                        )}
                        {isRitr && item.progetto_id && projById[item.progetto_id] && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white" style={{ background: projById[item.progetto_id].colore || "#9333EA" }}>{projById[item.progetto_id].nome}</span>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`ssd-menu-${item.id}`} onClick={(e) => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-slate-700 dark:hover:text-white">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setEditing(item); setOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`ssd-history-${item.id}`} onClick={() => setHistoryItem(item)}>
                            <History className="h-3.5 w-3.5 mr-2" /> Storico
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`ssd-attivita-${item.id}`} onClick={() => setAttivitaItem(item)}>
                            <CalendarDays className="h-3.5 w-3.5 mr-2" /> Attività
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`ssd-preventivo-${item.id}`} onClick={() => api.exportPreventivo(item.id, item.nome).then(() => toast.success("Preventivo PDF generato")).catch(() => toast.error("Nessuna voce nel listino dedicato"))}>
                            <FileText className="h-3.5 w-3.5 mr-2" /> Preventivo PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => remove(item)}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="flex items-center gap-3 mt-2.5 text-[11px] text-slate-500">
                      {item.citta && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{item.citta}</span>}
                      {item.atleti > 0 && <span className="inline-flex items-center gap-1"><Users2 className="h-3 w-3" />{item.atleti}</span>}
                      {item.lavori?.length > 0 && <span className="inline-flex items-center gap-1"><Layers className="h-3 w-3" />{item.lavori.length} lavori</span>}
                    </div>

                    {item.note?.trim() && <NotePreview item={item} />}
                    {isAcq(item, settings) && item.ricavo > 0 && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span data-testid={`ssd-ricavo-${item.id}`}
                              className="mt-2.5 inline-flex items-center gap-1 rounded-full border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-xs font-medium cursor-help">
                              <Euro className="h-3 w-3" /> Ricavo {eur(item.ricavo)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[240px] text-xs">
                            {item.lavori?.length > 0 ? `Somma di ${item.lavori.length} lavori. ` : ""}Compenso al collaboratore: <b>{eur2(compensoSsd(item, collab, comm, settings))}</b>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800">
                      <Avatar collab={collab} />
                      <div className="flex items-center gap-0.5">
                        <button data-testid={`move-back-${item.id}`} disabled={idx === 0} onClick={(e) => { e.stopPropagation(); move(item, -1); }}
                          className="h-6 w-6 rounded-md flex items-center justify-center text-slate-400 enabled:hover:bg-slate-100 dark:enabled:hover:bg-slate-800 disabled:opacity-30 transition-colors">
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button data-testid={`move-fwd-${item.id}`} disabled={idx === stagesList.length - 1} onClick={(e) => { e.stopPropagation(); move(item, 1); }}
                          className="h-6 w-6 rounded-md flex items-center justify-center text-slate-400 enabled:hover:bg-slate-100 dark:enabled:hover:bg-slate-800 disabled:opacity-30 transition-colors">
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {col.items.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 p-6 text-center text-xs text-slate-400">
                  {isRitr ? (isPartnerTab ? "Nessun partner" : "Nessun cliente") : "Nessuna SSD"}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <SsdDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }} onSave={save} collaboratori={collaboratori} initial={editing} canale={isRitr ? (editing?.canale === "partner" ? "partner" : editing ? "privati" : canale) : null} />

      <Dialog open={!!historyItem} onOpenChange={(v) => !v && setHistoryItem(null)}>
        <DialogContent className="max-w-md" data-testid="ssd-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-primary" /> Storico — {historyItem?.nome}
            </DialogTitle>
            <DialogDescription>Cronologia degli spostamenti lungo la pipeline.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {(historyItem?.storico || []).length === 0 && (
              <p className="text-sm text-slate-400">Nessuno spostamento registrato.</p>
            )}
            <ol className="relative border-l border-slate-200 dark:border-slate-800 ml-2 space-y-5">
              {(historyItem?.storico || []).map((h, i, arr) => {
                const label = stageLabel(h.stage);
                const dot = stageDot(stagesList.findIndex((s) => s.key === h.stage));
                const last = i === arr.length - 1;
                return (
                  <li key={i} className="ml-5">
                    <span className={`absolute -left-[7px] h-3.5 w-3.5 rounded-full border-2 border-white dark:border-slate-900 ${dot} ${last ? "ring-2 ring-primary/30" : ""}`} />
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</span>
                      <span className="text-xs text-slate-400 tabular-nums">{dateIt(h.at)}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!attivitaItem} onOpenChange={(v) => !v && setAttivitaItem(null)}>
        <DialogContent className="max-w-md" data-testid="ssd-attivita-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" /> Attività — {attivitaItem?.nome}
            </DialogTitle>
            <DialogDescription>Programmazione delle attività per questo {isRitr ? "cliente" : "soggetto"}.</DialogDescription>
          </DialogHeader>
          {attivitaItem && <AttivitaPanel fixedSsd={{ id: attivitaItem.id, tipo: "pipeline", nome: attivitaItem.nome }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
